import React, { useState, useCallback, useRef, useEffect } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Chat } from '@google/genai';
import { getAi, designSuggestionsSchema, generateDesignImage } from '../services/geminiService.ts';
import { DesignSuggestion, MeasurementSystem } from '../types.ts';
import Card, { CardContent, CardHeader, CardTitle, CardDescription } from './Card.tsx';
import Spinner from './Spinner.tsx';
import { DownloadIcon } from './icons/DownloadIcon.tsx';
import DesignSuggestionPDF from './DesignSuggestionPDF.tsx';

interface ChatMessage {
    role: 'user' | 'model';
    content: string | DesignSuggestion[];
}

const DesignGenerator: React.FC = () => {
    const [chatSession, setChatSession] = useState<Chat | null>(null);
    const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
    const [userInput, setUserInput] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [measurementSystem, setMeasurementSystem] = useState<MeasurementSystem>(MeasurementSystem.Imperial);
    
    // State for PDF generation
    const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
    const [suggestionToDownload, setSuggestionToDownload] = useState<DesignSuggestion | null>(null);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    
    // Scroll to the bottom of the chat on new message
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chatHistory, loading]);

    // Initialize Chat Session
    const initializeChat = useCallback(() => {
         try {
            const ai = getAi();
            const systemInstruction = `
                You are an expert furniture designer for woodworkers. Your task is to generate 2 to 3 distinct and practical design suggestions based on the user's request.
                The user has requested dimensions in the ${measurementSystem} system.
                For each suggestion, you must provide:
                1. A descriptive design name.
                2. A brief concept overview (2-3 sentences).
                3. The primary material.
                4. A detailed piece list with precise dimensions for every single wood component.
                
                Follow these rules strictly:
                - If the system is 'imperial', all dimensions MUST be in inches. The format must be a string: "[Length]" x [Width]" x [Thickness]". For example, "72\\" x 36\\" x 1.5\\"".
                - If the system is 'metric', all dimensions MUST be in millimeters (mm). The format must be a string: "[Length]mm x [Width]mm x [Thickness]mm". For example, "1800mm x 900mm x 38mm".
                - If the user doesn't specify dimensions, use standard, ergonomic proportions for the furniture type in the requested measurement system.
                - Provide practical "notes" for each piece, mentioning joinery, shaping, or other important details. If there are no special notes, return an empty string for the "notes" field.
                - Ensure your entire response is a valid JSON array matching the provided schema.
                - When the user asks for a modification, apply it to the previous suggestions and return the complete, updated list of designs.
            `;
            const newChat = ai.chats.create({
                model: 'gemini-2.5-flash',
                config: {
                    responseMimeType: 'application/json',
                    responseSchema: designSuggestionsSchema,
                    systemInstruction: systemInstruction,
                }
            });
            setChatSession(newChat);
            setChatHistory([]); // Clear history when measurement system changes
        } catch (err) {
            setError("Failed to initialize the AI chat session.");
            console.error(err);
        }
    }, [measurementSystem]);

    useEffect(() => {
        initializeChat();
    }, [initializeChat]);


    const handleSendMessage = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (!userInput.trim() || !chatSession) {
            return;
        }

        const userMessage: ChatMessage = { role: 'user', content: userInput };
        setChatHistory(prev => [...prev, userMessage]);
        setLoading(true);
        setError(null);
        const currentInput = userInput;
        setUserInput('');

        try {
            const response = await chatSession.sendMessage({ message: currentInput });
            const suggestions: DesignSuggestion[] = JSON.parse(response.text);
            
            const modelMessage: ChatMessage = { role: 'model', content: suggestions };
            setChatHistory(prev => [...prev, modelMessage]);
            setLoading(false);

            // Asynchronously fetch images for each new suggestion
            suggestions.forEach(async (suggestion, index) => {
                try {
                    const imagePrompt = `${suggestion.designName}: ${suggestion.conceptOverview}`;
                    const imageB64 = await generateDesignImage(imagePrompt);
                    const imageUrl = `data:image/jpeg;base64,${imageB64}`;

                    setChatHistory(prev => {
                        const updatedHistory = [...prev];
                        const lastMessage = updatedHistory[updatedHistory.length - 1];
                        if (lastMessage && lastMessage.role === 'model' && Array.isArray(lastMessage.content)) {
                            const newSuggestions = [...lastMessage.content];
                            if (newSuggestions[index]) {
                                newSuggestions[index] = { ...newSuggestions[index], imageUrl };
                                // Create a new message object to ensure React re-renders
                                updatedHistory[updatedHistory.length - 1] = { ...lastMessage, content: newSuggestions };
                            }
                        }
                        return updatedHistory;
                    });
                } catch (imageError) {
                    console.error(`Failed to generate image for "${suggestion.designName}":`, imageError);
                }
            });

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
            setError(`The AI returned an error. ${errorMessage}`);
            setLoading(false);
            console.error(err);
        }
    }, [userInput, chatSession]);
    
     const handleDownloadPdf = async (suggestion: DesignSuggestion) => {
        setSuggestionToDownload(suggestion);
        setIsDownloadingPdf(true);
        await new Promise(resolve => setTimeout(resolve, 100));

        const input = document.getElementById('pdf-render-content');
        if (input) {
            try {
                const canvas = await html2canvas(input, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' });
                const imgData = canvas.toDataURL('image/png');
                const pdf = new jsPDF('p', 'mm', 'a4');
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
                const pageHeight = pdf.internal.pageSize.getHeight();
                let heightLeft = pdfHeight, position = 0;

                pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
                heightLeft -= pageHeight;
                while (heightLeft > 0) {
                  position = heightLeft - pdfHeight;
                  pdf.addPage();
                  pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
                  heightLeft -= pageHeight;
                }
                pdf.save(`${suggestion.designName.replace(/\s/g, '_')}.pdf`);
            } catch (pdfError) {
                console.error("Failed to generate PDF", pdfError);
                setError("Sorry, there was an issue creating the PDF.");
            } finally {
                setSuggestionToDownload(null);
                setIsDownloadingPdf(false);
            }
        } else {
            setSuggestionToDownload(null);
            setIsDownloadingPdf(false);
        }
    };


    return (
        <div className="max-w-4xl mx-auto">
            <Card>
                <CardHeader>
                    <CardTitle>AI Design Generator</CardTitle>
                    <CardDescription>Describe a piece of furniture, then refine the AI's suggestions in a conversation.</CardDescription>
                </CardHeader>
                <CardContent>
                     <div className="flex justify-between items-center mb-4">
                        <label className="block text-sm font-medium text-stone-700">Measurement System</label>
                        <div className="inline-flex rounded-md shadow-sm">
                            <button onClick={() => setMeasurementSystem(MeasurementSystem.Imperial)} className={`px-4 py-2 text-sm font-medium rounded-l-lg border ${measurementSystem === MeasurementSystem.Imperial ? 'bg-amber-700 text-white border-amber-700 z-10' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'}`}>Imperial (in)</button>
                            <button onClick={() => setMeasurementSystem(MeasurementSystem.Metric)} className={`-ml-px px-4 py-2 text-sm font-medium rounded-r-lg border ${measurementSystem === MeasurementSystem.Metric ? 'bg-amber-700 text-white border-amber-700 z-10' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'}`}>Metric (mm)</button>
                        </div>
                    </div>

                    <div ref={chatContainerRef} className="h-[60vh] overflow-y-auto p-4 bg-stone-50 border rounded-lg space-y-4">
                        {chatHistory.length === 0 && (
                            <div className="text-center text-stone-500 h-full flex items-center justify-center">
                                <p>Start by describing the furniture you'd like to build.</p>
                            </div>
                        )}
                        {chatHistory.map((msg, index) => (
                            <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                {msg.role === 'user' ? (
                                    <div className="bg-amber-700 text-white p-3 rounded-lg max-w-lg shadow">
                                        {typeof msg.content === 'string' && msg.content}
                                    </div>
                                ) : (
                                    <div className="bg-white p-3 rounded-lg max-w-full w-full border shadow">
                                        {Array.isArray(msg.content) && msg.content.map((suggestion, sIndex) => (
                                            <div key={sIndex} className="mb-6 last:mb-0 border-b last:border-b-0 pb-4 last:pb-0">
                                                <div className="flex justify-between items-center gap-4 mb-4">
                                                    <h3 className="text-lg font-bold text-stone-800">Suggestion {sIndex + 1}: <span className="text-amber-800">{suggestion.designName}</span></h3>
                                                    <button 
                                                        onClick={() => handleDownloadPdf(suggestion)}
                                                        disabled={isDownloadingPdf || !suggestion.imageUrl}
                                                        className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 bg-amber-700 text-white font-semibold rounded-lg shadow-sm hover:bg-amber-800 disabled:bg-stone-400 disabled:cursor-not-allowed transition-colors text-sm"
                                                        title={!suggestion.imageUrl ? "Wait for image to generate" : "Download as PDF"}
                                                    >
                                                        <DownloadIcon className="h-4 w-4" /> PDF
                                                    </button>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                                                    <div className="w-full aspect-4/3 bg-stone-100 rounded-lg flex items-center justify-center">
                                                        {suggestion.imageUrl ? <img src={suggestion.imageUrl} alt={suggestion.designName} className="w-full h-full object-cover rounded-lg" /> : <Spinner text="Generating..." />}
                                                    </div>
                                                    <div className="space-y-2">
                                                        <div><h4 className="font-semibold">Overview</h4><p className="text-sm text-stone-600">{suggestion.conceptOverview}</p></div>
                                                        <div><h4 className="font-semibold">Material</h4><p className="text-sm text-stone-600">{suggestion.material}</p></div>
                                                    </div>
                                                </div>
                                                <div className="mt-4 overflow-x-auto">
                                                    <table className="min-w-full divide-y divide-stone-200 border text-sm">
                                                        <thead className="bg-stone-50"><tr><th className="px-2 py-1 text-left font-medium">Piece</th><th className="px-2 py-1 text-left font-medium">Dimensions</th><th className="px-2 py-1 text-left font-medium">Notes</th></tr></thead>
                                                        <tbody className="bg-white divide-y divide-stone-200">
                                                            {suggestion.pieceList.map((p, pIdx) => <tr key={pIdx}><td className="px-2 py-1">{p.pieceName}</td><td className="px-2 py-1">{p.dimensions}</td><td className="px-2 py-1">{p.notes || 'N/A'}</td></tr>)}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                         {loading && (
                            <div className="flex justify-start">
                                <div className="bg-white p-3 rounded-lg border shadow">
                                    <Spinner text="Thinking..." />
                                </div>
                            </div>
                        )}
                        {error && <div className="text-center text-red-600 bg-red-100 p-2 rounded-md text-sm">{error}</div>}
                    </div>

                    <form onSubmit={handleSendMessage} className="mt-4 flex gap-2">
                        <input
                            type="text"
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            className="flex-grow p-2 border border-stone-300 rounded-md shadow-sm focus:ring-amber-500 focus:border-amber-500"
                            placeholder="e.g., I like suggestion 2, but make the tabletop round."
                            disabled={loading || isDownloadingPdf}
                        />
                        <button
                            type="submit"
                            disabled={loading || isDownloadingPdf || !userInput.trim()}
                            className="px-6 py-2 bg-amber-700 text-white font-semibold rounded-lg shadow-md hover:bg-amber-800 disabled:bg-stone-400 transition-colors"
                        >
                            Send
                        </button>
                    </form>
                </CardContent>
            </Card>

            {suggestionToDownload && (
                <div style={{ position: 'absolute', left: '-9999px', top: '0px', zIndex: -1 }}>
                    <DesignSuggestionPDF suggestion={suggestionToDownload} />
                </div>
            )}
        </div>
    );
};

export default DesignGenerator;